import * as React from 'react'
import { Logger } from 'src/3rds/cube'

import { makeStyles } from 'tss-react/mui'

import Autocomplete, { type AutocompleteProps, createFilterOptions } from '@mui/material/Autocomplete'
import Badge from '@mui/material/Badge'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Divider from '@mui/material/Divider'
import Drawer from '@mui/material/Drawer'
import IconButton from '@mui/material/IconButton'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'

import CloseIcon from '@mui/icons-material/Close'
import FilterListIcon from '@mui/icons-material/FilterList'

import {
    cnpjsDaLista,
    etiquetasDosFiltros,
    listaEmTexto,
    negociosDaLista,
    numero,
    rotuloClassificacao,
    rotuloMunicipio,
    semGrupo
} from '../Consultas.graficos'
import {
    DashboardsScope,
    FILTROS_CONSULTAS_VAZIOS,
    type FiltrosConsultas,
    type ParceiroEncontrado
} from '../Dashboards.scopes'

const LOG = Logger.get('ConsultasFiltros-VIEW')

/** Mesmo limite do servidor para as listas coladas. */
const MAXIMO_NA_LISTA = 5000

// Milhares de municípios: o Autocomplete mostra só os primeiros que batem com o que foi digitado.
const filtrarMunicipios = createFilterOptions<string>({ limit: 100, stringify: rotuloMunicipio })

const useStyles = makeStyles()((theme) => ({
    barra: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        flexWrap: 'wrap'
    },
    gaveta: {
        width: 400,
        maxWidth: '100vw',
        display: 'flex',
        flexDirection: 'column',
        height: '100%'
    },
    topo: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: theme.spacing(2, 2, 1, 2.5)
    },
    campos: {
        flex: 1,
        overflow: 'auto',
        padding: theme.spacing(1, 2.5, 2),
        display: 'flex',
        flexDirection: 'column',
        rowGap: theme.spacing(2)
    },
    rodape: {
        display: 'flex',
        justifyContent: 'space-between',
        gap: theme.spacing(1),
        padding: theme.spacing(1.5, 2.5)
    },
    secao: {
        fontSize: 13,
        fontWeight: 600,
        color: theme.palette.text.secondary,
        textTransform: 'uppercase',
        letterSpacing: 0.4
    }
}))

/** O que a gaveta edita antes de aplicar: os filtros, as listas como texto e os parceiros com nome. */
type Rascunho = FiltrosConsultas & {
    textoCnpjs: string
    textoNegocios: string
    parceirosEscolhidos: ParceiroEncontrado[]
}

function rascunhoDe(filtros: FiltrosConsultas, nomes: Record<string, string>): Rascunho {
    return {
        ...filtros,
        textoCnpjs: listaEmTexto(filtros.cnpjs),
        textoNegocios: listaEmTexto(filtros.negocios),
        parceirosEscolhidos: filtros.parceiros.map((ownerId) => ({
            ownerId,
            nome: nomes[ownerId] ?? ownerId,
            email: ''
        }))
    }
}

/** Botão "Filtros" e as etiquetas dos filtros em uso, cada uma com o X para tirar. */
export function BarraDeFiltros({ scope }: { scope: DashboardsScope }) {
    const { classes } = useStyles()
    const [aberto, setAberto] = React.useState(false)
    const etiquetas = etiquetasDosFiltros(scope.filtrosConsultas, scope.nomesParceiros)

    const aplicar = (filtros: FiltrosConsultas, nomes?: Record<string, string>) =>
        scope.onFiltrosConsultasChange({ filtros, nomes }).catch(LOG.caught)

    return (
        <Box className={classes.barra}>
            <Badge badgeContent={etiquetas.length} color="primary" overlap="rectangular">
                <Button variant="outlined" size="small" startIcon={<FilterListIcon />} onClick={() => setAberto(true)}>
                    Filtros
                </Button>
            </Badge>
            {etiquetas.map((e) => (
                <Chip
                    key={e.grupo}
                    size="small"
                    label={e.rotulo}
                    onDelete={() => aplicar(semGrupo(scope.filtrosConsultas, e.grupo))}
                    disabled={scope.carregando}
                />
            ))}
            {etiquetas.length > 1 && (
                <Button size="small" onClick={() => aplicar(FILTROS_CONSULTAS_VAZIOS)} disabled={scope.carregando}>
                    Limpar filtros
                </Button>
            )}
            <GavetaDeFiltros scope={scope} aberto={aberto} onFechar={() => setAberto(false)} onAplicar={aplicar} />
        </Box>
    )
}

function GavetaDeFiltros({
    scope,
    aberto,
    onFechar,
    onAplicar
}: {
    scope: DashboardsScope
    aberto: boolean
    onFechar: () => void
    onAplicar: (filtros: FiltrosConsultas, nomes?: Record<string, string>) => void
}) {
    const { classes } = useStyles()
    const opcoes = scope.opcoesConsultas
    const [r, setR] = React.useState<Rascunho>(() => rascunhoDe(scope.filtrosConsultas, scope.nomesParceiros))

    // Ao abrir, parte dos filtros em uso (que podem ter mudado pelas etiquetas ou por um clique no painel).
    React.useEffect(() => {
        if (aberto) setR(rascunhoDe(scope.filtrosConsultas, scope.nomesParceiros))
    }, [aberto, scope.filtrosConsultas, scope.nomesParceiros])

    const mudar = <K extends keyof Rascunho>(campo: K, valor: Rascunho[K]) =>
        setR((atual) => ({ ...atual, [campo]: valor }))

    const cnpjs = React.useMemo(() => cnpjsDaLista(r.textoCnpjs), [r.textoCnpjs])
    const negocios = React.useMemo(() => negociosDaLista(r.textoNegocios), [r.textoNegocios])
    const listaLonga = cnpjs.validos.length > MAXIMO_NA_LISTA || negocios.validos.length > MAXIMO_NA_LISTA

    // Municípios e divisões acompanham as UFs e seções escolhidas.
    const municipios = React.useMemo(
        () =>
            (opcoes?.municipios ?? [])
                .filter((m) => !r.ufs.length || r.ufs.includes(m.uf))
                .map((m) => `${m.uf}|${m.municipio}`),
        [opcoes, r.ufs]
    )
    const divisoes = React.useMemo(
        () => [
            ...new Set(
                (opcoes?.divisoes ?? [])
                    .filter((d) => !r.secoes.length || r.secoes.includes(d.secao))
                    .map((d) => d.divisao)
            )
        ],
        [opcoes, r.secoes]
    )
    const consultasPorDesfecho = React.useMemo(
        () => new Map((opcoes?.classificacoes ?? []).map((c) => [c.valor, c.consultas])),
        [opcoes]
    )

    const aplicar = () => {
        const { textoCnpjs, textoNegocios, parceirosEscolhidos, ...filtros } = r
        void textoCnpjs
        void textoNegocios
        onAplicar(
            {
                ...filtros,
                cnae: filtros.cnae.trim(),
                parceiros: parceirosEscolhidos.map((p) => p.ownerId),
                cnpjs: cnpjs.validos,
                negocios: negocios.validos
            },
            Object.fromEntries(parceirosEscolhidos.map((p) => [p.ownerId, p.nome]))
        )
        onFechar()
    }

    const multiplo = (
        rotulo: string,
        campo: 'classificacoes' | 'ufs' | 'municipios' | 'secoes' | 'divisoes' | 'naturezas',
        lista: string[],
        extra: Partial<
            Pick<AutocompleteProps<string, true, false, false>, 'getOptionLabel' | 'renderOption' | 'filterOptions'>
        > = {}
    ) => (
        <Autocomplete<string, true>
            multiple
            size="small"
            options={lista}
            value={r[campo]}
            onChange={(_evt, valor) => mudar(campo, valor)}
            renderInput={(params) => <TextField {...params} label={rotulo} />}
            noOptionsText="Nada encontrado"
            loading={!opcoes}
            loadingText="Carregando…"
            {...extra}
        />
    )

    return (
        <Drawer anchor="right" open={aberto} onClose={onFechar}>
            <Box className={classes.gaveta} role="dialog" aria-label="Filtros do dashboard de consultas">
                <Box className={classes.topo}>
                    <Typography variant="h6" fontWeight={700}>
                        Filtros
                    </Typography>
                    <IconButton onClick={onFechar} aria-label="Fechar os filtros">
                        <CloseIcon />
                    </IconButton>
                </Box>
                <Divider />
                <Box className={classes.campos}>
                    <Typography className={classes.secao}>Consulta</Typography>
                    {multiplo(
                        'Desfecho',
                        'classificacoes',
                        (opcoes?.classificacoes ?? []).map((c) => c.valor),
                        {
                            getOptionLabel: rotuloClassificacao,
                            renderOption: (props, valor) => (
                                <li {...props} key={valor}>
                                    <Box
                                        sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', gap: 2 }}
                                    >
                                        <span>{rotuloClassificacao(valor)}</span>
                                        <Typography component="span" color="text.secondary" fontSize={13}>
                                            {numero(consultasPorDesfecho.get(valor) ?? 0)}
                                        </Typography>
                                    </Box>
                                </li>
                            )
                        }
                    )}
                    <Autocomplete<ParceiroEncontrado, true>
                        multiple
                        size="small"
                        options={scope.sugestoesParceiro}
                        value={r.parceirosEscolhidos}
                        onChange={(_evt, valor) => mudar('parceirosEscolhidos', valor)}
                        onInputChange={(_evt, texto, motivo) => {
                            if (motivo === 'input') scope.onBuscarSugestaoParceiro(texto).catch(LOG.caught)
                        }}
                        filterOptions={(x) => x}
                        isOptionEqualToValue={(a, b) => a.ownerId === b.ownerId}
                        getOptionLabel={(p) => p.nome}
                        renderOption={(props, p) => (
                            <li {...props} key={p.ownerId}>
                                <Box>
                                    <div>{p.nome}</div>
                                    <Typography color="text.secondary" fontSize={12}>
                                        {p.email} · {p.ownerId}
                                    </Typography>
                                </Box>
                            </li>
                        )}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Parceiro que consultou"
                                placeholder="Nome, e-mail ou owner_id"
                            />
                        )}
                        noOptionsText="Digite ao menos 2 letras"
                    />

                    <Typography className={classes.secao}>Empresa</Typography>
                    {multiplo('UF', 'ufs', opcoes?.ufs ?? [])}
                    {multiplo('Município', 'municipios', municipios, {
                        getOptionLabel: rotuloMunicipio,
                        filterOptions: filtrarMunicipios
                    })}
                    {multiplo('Seção do CNAE', 'secoes', opcoes?.secoes ?? [])}
                    {multiplo('Divisão do CNAE', 'divisoes', divisoes)}
                    <TextField
                        size="small"
                        label="CNAE"
                        placeholder="Código (4744) ou parte da descrição ou do grupo"
                        value={r.cnae}
                        onChange={(evt) => mudar('cnae', evt.target.value)}
                    />
                    {multiplo('Natureza jurídica', 'naturezas', opcoes?.naturezas ?? [])}

                    <Typography className={classes.secao}>Filtro de lista</Typography>
                    <TextField
                        size="small"
                        label="Lista de CNPJs"
                        placeholder="Cole os CNPJs, um por linha"
                        multiline
                        minRows={3}
                        maxRows={8}
                        value={r.textoCnpjs}
                        onChange={(evt) => mudar('textoCnpjs', evt.target.value)}
                        helperText={resumoDaLista(cnpjs, 'CNPJ', 'CNPJs')}
                        error={cnpjs.validos.length > MAXIMO_NA_LISTA}
                    />
                    <TextField
                        size="small"
                        label="Lista de negócios (deal_id)"
                        placeholder="Cole os deal_id, um por linha"
                        multiline
                        minRows={3}
                        maxRows={8}
                        value={r.textoNegocios}
                        onChange={(evt) => mudar('textoNegocios', evt.target.value)}
                        helperText={resumoDaLista(negocios, 'negócio', 'negócios')}
                        error={negocios.validos.length > MAXIMO_NA_LISTA}
                    />
                </Box>
                <Divider />
                <Box className={classes.rodape}>
                    <Button onClick={() => setR(rascunhoDe(FILTROS_CONSULTAS_VAZIOS, {}))}>Limpar tudo</Button>
                    <Button variant="contained" onClick={aplicar} disabled={listaLonga}>
                        Aplicar
                    </Button>
                </Box>
            </Box>
        </Drawer>
    )
}

function resumoDaLista(lista: { validos: string[]; invalidos: string[] }, um: string, varios: string): string {
    if (!lista.validos.length && !lista.invalidos.length) return 'Aceita a máscara e linhas em branco.'
    const partes = [`${numero(lista.validos.length)} ${lista.validos.length === 1 ? um : varios}`]
    if (lista.invalidos.length) {
        partes.push(`${numero(lista.invalidos.length)} ignorados (${lista.invalidos.slice(0, 2).join(', ')}…)`)
    }
    if (lista.validos.length > MAXIMO_NA_LISTA) partes.push(`no máximo ${numero(MAXIMO_NA_LISTA)}`)
    return partes.join(' · ')
}

export default BarraDeFiltros
